import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericButton from "@ui-components/Button/GenericButton";
import { formatContractDate, withFallback } from "kontrolEdge/helpers/formattersShappedData";
import { ResourceTagsTableCell } from "../Shared/ResourceTagsTableCell";
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { mapKeListResourceTags } from "../Shared/mapKeListResourceTags";
import {
    resolveServiceStatusTag,
    serviceStatusRank,
} from "kontrolEdge/helpers/serviceStatusTag";
import {
	shapeCarrierAccess,
	type CarrierAccess,
} from "kontrolEdge/helpers/carrierAccess";
import {
	buildPhysicalConnection,
	physicalConnectionIds,
	physicalConnectionType,
	type PhysicalConnection,
} from "kontrolEdge/helpers/physicalConnections";
import {
	extenderPopDetailPath,
	portLocationLabel,
	shapePortExtenderPop,
	type PortExtenderPop,
} from "kontrolEdge/helpers/portLocation";
import { DetailLink } from "../Shared/DetailLink";
import CarrierAccessSummary from "../ExtenderPop/CarrierAccessSummary";

export interface Port {
	id: number;
	portId: string;
	name: string;
	/** Rich tags from the Tags Manager pivot (§5.5). */
	tagsRich?: ResourceTagSummary[];
	lag: string[]
	renewDate: string;
	renewDateRaw: Date | null;
	term: string;
	serviceType: string;
	portType: string;
	latestStatus: string;
	capacity: number;
	crossConnectId: string;
	/** Carriers reaching this port; empty when the description names none. */
	carrierAccess: CarrierAccess[];
	/**
	 * How the port is physically reached — carrier access if it has one, else its
	 * cross connect. Computed once in `shapePortData` so the two columns, their
	 * sorting and the filter spec all read the same value.
	 */
	physicalConnection: PhysicalConnection | null;
	customer?: {
		name: string
		id: number;
		chargebeeCustomerId: string
	}
	datacenter: {
		name: string;
		datacenterId?: string;
	}
	/** The Extended PoP that delivers the port; its location when present (CTX-802). */
	extenderPop: PortExtenderPop | null;
	status?: string | null;
	state: {
		actualState: string
	}
	statusMessage?: string | null;
	latestStatustime: string;
	type?: string;
}

/**
 * One column instead of two, to keep the table inside the viewport: the type on
 * a muted first line and the identifiers on the second. Splitting these into
 * separate columns is what pushed the table into horizontal scroll.
 *
 * A port reached by two carriers joins them on the one line, so the row keeps a
 * fixed height regardless of how many carriers it has.
 */
const PhysicalConnectionCell: React.FC<{ row: Port }> = ({ row }) => {
	const connection = row.physicalConnection;
	if (!connection) return <div>-</div>;

	// Legs that back a sold Backhaul read as "84-… · carrier", on one line:
	// the redundancy needs every leg of the access, so it stays on its detail.
	// `port-backhaul` anchors the Backhaul walkthrough of the What's new entry
	// (changelog/entries/kontrol-edge/2026-10-27-backhaul-details.yml).
	if (connection.backhaulLegs) {
		return (
			<div className="flex flex-col leading-tight" data-testid="port-backhaul">
				<span className="text-xs text-semantic-text-secondary">
					Type: {connection.typeLabel}
				</span>
				<CarrierAccessSummary carriers={connection.backhaulLegs} compact partial />
			</div>
		);
	}

	return (
		<div className="flex flex-col leading-tight">
			<span className="text-xs text-semantic-text-secondary">
				Type: {connection.typeLabel}
			</span>
			{/* A local handoff whose media nobody recorded has no second line. */}
			{connection.entries.length > 0 && (
				<span className="whitespace-nowrap">
					<span className="text-xs text-semantic-text-secondary">
						{connection.valueLabel}:{" "}
					</span>
					{physicalConnectionIds(connection)}
				</span>
			)}
		</div>
	);
};

/**
 * Where the port is for the customer (CTX-802): the Extended PoP that
 * delivers it, linked to its detail, else the POP.
 */
const PortLocationCell: React.FC<{ row: Port }> = ({ row }) => {
	if (!row.extenderPop) return <div>{portLocationLabel(row)}</div>;

	return (
		<div className="flex flex-col leading-tight">
			<span className="text-xs text-semantic-text-secondary">CPE</span>
			<DetailLink
				to={extenderPopDetailPath(row.extenderPop)}
				label={portLocationLabel(row)}
			/>
		</div>
	);
};

export const portsColumns: GenericTableColumn<Port>[] = [
	{
		header: "Port ID",
		accessor: "portId",
		renderCell: (row) => <GenericButton variant="brand" label={row.portId} />,
	},
	{
		header: "Name",
		accessor: "name"
	},
	{
		header: "Status",
		renderCell: row => {
			const status = row.status ?? row.state?.actualState;
			const { label, variant } = resolveServiceStatusTag(status);
			return <GenericTag variant={variant} label={label} size="small" />;
		},
		sortAccessor: (row) => serviceStatusRank(row.status ?? row.state?.actualState),
	},
	{
		header: "Capacity",
		renderCell: (row) => <div>{row.capacity/ 1000} Gbps</div>,
	},
	{
		header: "Tags",
		renderCell: (row) => (
			<ResourceTagsTableCell
				tagsRich={row.tagsRich}
				maxVisible={1}
				triggerSingleRow
			/>
		),
	},
	{
		header: "POP",
		renderCell: (row) => <PortLocationCell row={row} />,
		sortAccessor: (row) => portLocationLabel(row),
	},
	{
		// Replaces the old "Cross Connect ID": a port can be reached by a cross
		// connect or by a carrier access (Backhaul), so the type has to be named
		// instead of implied. When it has both, the Backhaul wins.
		header: "Physical Connect",
		renderCell: (row) => <PhysicalConnectionCell row={row} />,
		sortAccessor: (row) =>
			`${physicalConnectionType(row.physicalConnection)} ${physicalConnectionIds(row.physicalConnection)}`,
	},
	{
		header: "Term",
		renderCell: (row) => <div>{row.term}</div>,
	},
	{
		header: "Renew Date",
		accessor: "renewDate",
	},
	{
		header: "Details",

		accessor: "name",
		renderCell: (row) => (
			<div>
				{/* View Button */}
				<button
					className="
            px-3 py-1
            border border-semantic-border-secondary
            rounded
            text-semantic-text-secondary
            hover:bg-semantic-state-active
          "
					onClick={() => {
						console.log("View port detail:", row);
					}}
				>
					View
				</button>
			</div>
		),
	},
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const shapePortData = (ports: any[]): Port[] => {
	return ports.map((p) => {
		const customer = p.customer
		? {
			name: p.customer.name ?? "-",
			id: p.customer.id ?? "-",
			chargebeeCustomerId: p.customer.chargebeeCustomerId ?? "-"
			}
		: undefined;

		const rawDate = p.renewDate ? new Date(p.renewDate) : null;
		const capNum = Number(
		typeof p.capacity === "number" ? p.capacity : String(p.capacity ?? "").replace(/,/g, "")
		);
		const dcName = p.datacenter?.name ?? "-";
		const dcId   = p.datacenter?.datacenterId ?? undefined;
		const tagsRich = mapKeListResourceTags(p);
		const carrierAccess = shapeCarrierAccess(p);
		const extenderPop = shapePortExtenderPop(p.extenderPop);
		const physicalConnection = buildPhysicalConnection({
			crossConnectId: p.crossConnectId,
			// The list response flattens the cross connect to its provider id, so
			// the media/speed text only exists on the detail response.
			crossConnectDetails: p.crossConnect?.details ?? null,
			carriers: carrierAccess,
			extenderPop,
			media: p.media ?? null,
		});

	return {
		id: p.id,
		portId: withFallback(p.portId),
		name: withFallback(p.name),
		customer,
		crossConnectId: p.crossConnectId,
		carrierAccess,
		physicalConnection,
		renewDateRaw: rawDate && !isNaN(rawDate.getTime()) ? rawDate : null,
		capacity: Number.isFinite(capNum) ? capNum : 0,
		renewDate: formatContractDate(p.renewDate),
		term: withFallback(p.term) + " " + withFallback(p.term_unit),
		serviceType: withFallback(p.type),
		portType:withFallback(p.portType),
		latestStatus: withFallback(p.latestStatus),
		latestStatustime: withFallback(p.latestStatustime),
		status: p.status ?? null,
		state: {
			actualState: withFallback(p.state?.actualState),
		},
		statusMessage: p.statusMessage ?? null,
		datacenter: {
			name: dcName,
			datacenterId: dcId,
		},
		extenderPop,
		tagsRich,
		lag: Array.isArray(p.lag) ? p.lag : [],
	}});
  };

